import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../i18n/LanguageContext';
import { api } from '../../services/api';
import type { NotificationPreferences as NotifPrefs } from '../../types/settings';

export default function NotificationPreferences() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [prefs, setPrefs] = useState<NotifPrefs | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    api.getNotificationPreferences(token)
      .then(setPrefs)
      .catch(() => setPrefs({ pushEnabled: true, emailEnabled: true, smsEnabled: false, promotionalEnabled: true }))
      .finally(() => setLoading(false));
  }, [token]);

  const toggle = async (key: keyof NotifPrefs) => {
    if (!prefs || !token) return;
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    try {
      await api.updateNotificationPreferences(updated, token);
    } catch {
      setPrefs(prefs); // revert on failure
    }
  };

  if (loading) return <div className="settings-section"><p>{t('common.loading')}</p></div>;
  if (!prefs) return null;

  return (
    <div className="settings-section">
      <h3 className="settings-section-title">{t('settings.notificationPreferences')}</h3>
      <div className="notification-toggles">
        <ToggleRow label={t('settings.emailAlerts')} checked={prefs.emailEnabled} onChange={() => toggle('emailEnabled')} />
        <ToggleRow label={t('settings.smsAlerts')} checked={prefs.smsEnabled} onChange={() => toggle('smsEnabled')} />
        <ToggleRow label={t('settings.pushNotifications')} checked={prefs.pushEnabled} onChange={() => toggle('pushEnabled')} />
      </div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return (
    <div className="notification-toggle-row">
      <span>{label}</span>
      <label className="toggle-switch">
        <input type="checkbox" checked={checked} onChange={onChange} />
        <span className="toggle-slider" />
      </label>
    </div>
  );
}
